import {
  Action,
  type ActionData,
  type ExecuteActionRequest,
  type ExecuteActionResponse,
  type GetCaseRecordResponse,
} from '@compass/client/types/case_record';
import type { UserRole } from '@compass/client/types/membership';
import { ACTION_CATEGORIES, ACTION_CONFIRMATION_MESSAGES, ACTION_LABELS } from '@constants/actions';
import { CaseState, KnipperCaseState } from '@constants/caseStates';
import { WorkflowType } from '@constants/dispositions';
import { caseRecordService, manualActionService } from '@services/api';
import { toast } from 'sonner';

import type { ServiceOptions } from '@/services/api.config';
import type { ActionDataParams, ActionItem, CategorizedActionsResult } from '@/types/actions';

interface ActionDialogState {
  isOpen: boolean;
  action: string;
  label: string;
}

interface ExecuteActionParams {
  caseId: string;
  action: Action;
  actionDataParams?: ActionDataParams;
  skipToast?: boolean;
}

interface ExecuteActionCallbacks {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

// Helper function to determine workflow prefix from workflow type
const getWorkflowPrefix = (workflowType?: string): string => {
  if (!workflowType) return 'This action is not valid';
  //This case is to handle benefit_verification workflow_type and workflowPrefix mismatch
  if (workflowType === 'benefit_verification') return 'BV';
  if (workflowType === 'medicare_human') return 'MEDICARE_HUMAN';
  return workflowType.toUpperCase();
};

// Common actions map (workflow-independent)
const COMMON_ACTIONS_MAP: Record<string, Action> = {
  human_call_mark_as_completed: Action.ACTION_HUMAN_CALL_MARK_AS_COMPLETED,
  human_call_mark_as_terminated: Action.ACTION_HUMAN_CALL_MARK_AS_TERMINATED,
  cancel_workflow: Action.ACTION_CANCEL_WORKFLOW,
  cancel_call: Action.ACTION_CANCEL_CALL,
  // Refill-only (JNJ-109): cancels the SCHEDULED retry attempt of a parked case,
  // which terminates it. Distinct from cancel_call, which hangs up a RINGING call
  // and lets the case carry on — see the proto comment on the action.
  refill_cancel_scheduled_call: Action.ACTION_REFILL_CANCEL_SCHEDULED_CALL,
  // Refill-only (JNJ-172): release a case parked in human_audit_needed. The
  // dialog's optional note is forwarded as the signal payload server-side.
  refill_human_audit_retry: Action.ACTION_REFILL_HUMAN_AUDIT_RETRY,
  refill_human_audit_skip: Action.ACTION_REFILL_HUMAN_AUDIT_SKIP,
  // Refill-only: complete a case parked in human_call_needed. The required note
  // is forwarded as the signal payload server-side.
  refill_mark_case_as_completed: Action.ACTION_REFILL_MARK_CASE_AS_COMPLETED,
  // PBM-only — fired while the case is in `ai_audit` state to rollback to manual review.
  cancel_ai_audit: Action.ACTION_CANCEL_AI_AUDIT,
  // Starts a human-audit session (BI-1246); routes through the generic /action endpoint.
  start_human_audit: Action.ACTION_START_HUMAN_AUDIT,
  regenerate_case_summary: Action.ACTION_REGENERATE_CASE_SUMMARY,
  submit_for_review: Action.ACTION_PQC_SUBMIT_FOR_REVIEW,
  pqc_mark_as_completed: Action.ACTION_PQC_MARK_AS_COMPLETED,
  pqc_resend_email: Action.ACTION_PQC_RESEND_EMAIL,
  mark_as_duplicate: Action.ACTION_PQC_MARK_AS_DUPLICATE,
  attach_to_case: Action.ACTION_PQC_ATTACH_TO_CASE,
  start_portal_session: Action.ACTION_PQC_START_PORTAL_SESSION,
  close_portal_session: Action.ACTION_PQC_CLOSE_PORTAL_SESSION,
  pqc_update_output: Action.ACTION_PQC_UPDATE_OUTPUT,
};

// Build workflow-specific action map (actionKey -> actionEnumString)
const getWorkflowActionMap = (workflowPrefix: string): Record<string, string> => ({
  submit_for_review: 'ACTION_PQC_SUBMIT_FOR_REVIEW',
  submit_to_manufacturer: 'ACTION_PQC_SUBMIT_TO_MANUFACTURER',
  pqc_resend_email: 'ACTION_PQC_RESEND_EMAIL',
  attach_to_case: 'ACTION_PQC_ATTACH_TO_CASE',
  call_with_ai: `ACTION_${workflowPrefix}_AUDIT_CALL_WITH_AI`,
  call_with_human: `ACTION_${workflowPrefix}_AUDIT_CALL_WITH_HUMAN`,
  mark_as_completed: `ACTION_${workflowPrefix}_AUDIT_MARK_AS_COMPLETED`,
  mark_as_terminated: `ACTION_${workflowPrefix}_AUDIT_MARK_AS_TERMINATED`,
  retry_call: `ACTION_${workflowPrefix}_AUDIT_RETRY_CALL`,
  human_call_mark_as_completed: `ACTION_${workflowPrefix}_CALL_MARK_AS_COMPLETED`,
  human_call_mark_as_failed: `ACTION_${workflowPrefix}_CALL_MARK_AS_FAILED`,
  // Move to Human Audit (BV-2514, BV-2657): resolves to ACTION_TRANSPORTATION_… or
  // ACTION_UNIFIEDMEDBV_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT; other workflows have no such action.
  human_call_move_to_human_audit: `ACTION_${workflowPrefix}_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT`,
  eligibility_check_retry: `ACTION_${workflowPrefix}_ELIGIBILITY_CHECK_RETRY`,
  eligibility_check_proceed: `ACTION_${workflowPrefix}_ELIGIBILITY_CHECK_PROCEED`,
  mark_case_completed: `ACTION_${workflowPrefix}_MARK_CASE_COMPLETED`,
  call_review_completed: `ACTION_${workflowPrefix}_CALL_REVIEW_COMPLETED`,
  call_review_failed: `ACTION_${workflowPrefix}_CALL_REVIEW_FAILED`,
  call_review_rejected: `ACTION_${workflowPrefix}_CALL_REVIEW_REJECTED`,
  case_completion_review_approved: `ACTION_${workflowPrefix}_CASE_COMPLETION_REVIEW_APPROVED`,
  case_completion_review_needs_revision: `ACTION_${workflowPrefix}_CASE_COMPLETION_REVIEW_NEEDS_REVISION`,
  case_completion_review_rejected: `ACTION_${workflowPrefix}_CASE_COMPLETION_REVIEW_REJECTED`,
  ineligible_case_completion_review_approved: `ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_APPROVED`,
  ineligible_case_completion_review_rejected: `ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_REJECTED`,
  update_note: `ACTION_${workflowPrefix}_AUDIT_UPDATE_NOTE`,
  create_task: `ACTION_${workflowPrefix}_AUDIT_CREATE_TASK`,
  send_text: `ACTION_${workflowPrefix}_AUDIT_SEND_TEXT`,
  send_email: `ACTION_${workflowPrefix}_AUDIT_SEND_EMAIL`,
  close_referral_fax: `ACTION_${workflowPrefix}_AUDIT_CLOSE_REFERRAL_FAX`,
  mark_as_ineligible: 'ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_INELIGIBLE',
  reachout_ineligible_create_task: 'ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_CREATE_TASK',
  reachout_ineligible_send_text: 'ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_SEND_TEXT',
  update_ineligibility_note:
    'ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_UPDATE_INELIGIBILITY_NOTE',
  reachout_ineligible_mark_as_terminated:
    'ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_MARK_AS_TERMINATED',
  mark_as_eligible: 'ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_MARK_AS_ELIGIBLE',
  // UnifiedMedBV EC Human Audit actions
  reinitiate_ec: `ACTION_${workflowPrefix}_EC_REINITIATE_EC`,
  move_to_ai_call: `ACTION_${workflowPrefix}_EC_MOVE_TO_AI_CALL`,
  move_to_human_call: `ACTION_${workflowPrefix}_EC_MOVE_TO_HUMAN_CALL`,
  move_to_medicare: `ACTION_${workflowPrefix}_EC_MOVE_TO_MEDICARE`,
  complete: `ACTION_${workflowPrefix}_EC_COMPLETE`,
  terminate: `ACTION_${workflowPrefix}_EC_TERMINATE`,
  // UnifiedMedBV Human Audit actions
  reinitiate_ai_call: `ACTION_${workflowPrefix}_AUDIT_REINITIATE_AI_CALL`,
  audit_move_to_human_call: `ACTION_${workflowPrefix}_AUDIT_MOVE_TO_HUMAN_CALL`,
  audit_move_to_medicare: `ACTION_${workflowPrefix}_AUDIT_MOVE_TO_MEDICARE`,
  // Referral Scheduler fax verification actions
  mark_fax_as_ineligible: `ACTION_${workflowPrefix}_FAX_VERIFICATION_MARK_FAX_AS_INELIGIBLE`,
  confirm_and_continue: `ACTION_${workflowPrefix}_FAX_VERIFICATION_CONFIRM_AND_CONTINUE`,
  // Referral Scheduler fax ineligible actions
  mark_fax_as_eligible: `ACTION_${workflowPrefix}_FAX_INELIGIBLE_MARK_FAX_AS_ELIGIBLE`,
  fax_ineligible_mark_as_terminated: `ACTION_${workflowPrefix}_FAX_INELIGIBLE_MARK_AS_TERMINATED`,
  // Referral Scheduler ineligible actions
  ineligible_mark_as_eligible: `ACTION_${workflowPrefix}_INELIGIBLE_MARK_AS_ELIGIBLE`,
  ineligible_mark_as_terminated: `ACTION_${workflowPrefix}_INELIGIBLE_MARK_AS_TERMINATED`,
  // Referral Scheduler pre-processing failed actions
  pre_processing_failed_confirm_and_continue: `ACTION_${workflowPrefix}_PRE_PROCESSING_FAILED_CONFIRM_AND_CONTINUE`,
  pre_processing_failed_mark_as_ineligible: `ACTION_${workflowPrefix}_PRE_PROCESSING_FAILED_MARK_AS_INELIGIBLE`,
  // Referral Scheduler missing input failure actions
  missing_input_completed: `ACTION_${workflowPrefix}_MISSING_INPUT_COMPLETED`,
  // Referral Scheduler pre-reachout review actions
  approved_for_call: `ACTION_${workflowPrefix}_PRE_REACHOUT_REVIEW_APPROVE_FOR_CALL`,
  pre_reachout_mark_as_ineligible: `ACTION_${workflowPrefix}_PRE_REACHOUT_REVIEW_MARK_AS_INELIGIBLE`,
});

// Helper function to get workflow-specific action
const getWorkflowSpecificAction = (
  actionKey: string,
  workflowPrefix: string,
): Action | undefined => {
  const actionMap = getWorkflowActionMap(workflowPrefix);
  const actionEnumKey = actionMap[actionKey];
  return actionEnumKey ? Action[actionEnumKey as keyof typeof Action] : undefined;
};

// Get the action enum based on action key, workflow type, and case state
export const getActionEnum = (
  action: string,
  workflowType?: string,
  caseState?: string,
): Action | undefined => {
  const actionKey = action.toLowerCase();

  // Terminate from missing-info has no workflow-specific audit action; the
  // workflow-prefixed ACTION_*_AUDIT_MARK_AS_TERMINATED is rejected by the
  // backend in this state. Dispatch the universal cancel-workflow instead.
  if (actionKey === 'mark_as_terminated' && caseState === CaseState.HUMAN_STEP_MISSING_INFO) {
    return Action.ACTION_CANCEL_WORKFLOW;
  }

  // Check if it's a common action
  if (COMMON_ACTIONS_MAP[actionKey]) {
    return COMMON_ACTIONS_MAP[actionKey];
  }

  // Handle workflow-specific actions
  const workflowPrefix = getWorkflowPrefix(workflowType);
  return getWorkflowSpecificAction(actionKey, workflowPrefix);
};

/**
 * Convert an action name string (e.g., "ACTION_BV_AUDIT_CALL_WITH_AI") to the Action enum value.
 * This is useful when the backend returns the action as a string (stored in DB) but we need the numeric enum.
 * @param actionName - The action name string (e.g., "ACTION_BV_AUDIT_CALL_WITH_AI")
 * @returns The Action enum value, or undefined if not found
 */
export const parseActionFromString = (actionName: string | undefined): Action | undefined => {
  if (!actionName) return undefined;
  const actionValue = Action[actionName as keyof typeof Action];
  return typeof actionValue === 'number' ? actionValue : undefined;
};

// Reverse lookup: get action key from action enum
// e.g., Action.ACTION_BV_AUDIT_CALL_WITH_AI -> "call_with_ai"
export const getActionKeyFromEnum = (actionEnum: Action, workflowType?: string): string | null => {
  // Check common actions first
  for (const [key, value] of Object.entries(COMMON_ACTIONS_MAP)) {
    if (value === actionEnum) {
      return key;
    }
  }

  // Check workflow-specific actions using Action enum name
  const workflowPrefix = getWorkflowPrefix(workflowType);
  const actionMap = getWorkflowActionMap(workflowPrefix);
  const actionName = Action[actionEnum]; // Convert numeric enum to string name
  for (const [key, value] of Object.entries(actionMap)) {
    if (value === actionName) {
      return key;
    }
  }

  return null;
};

// Get action label from workflow configuration
export const getActionLabelFromWorkflow = (actionEnum: Action, workflow: any): string | null => {
  const actionKey = getActionKeyFromEnum(actionEnum, workflow?.workflow_type);
  if (!actionKey || !workflow?.ui?.status) {
    return null;
  }

  // Search through all status configurations to find the action label
  const statusConfig = workflow.ui.status;
  for (const stateKey of Object.keys(statusConfig)) {
    const actions = statusConfig[stateKey]?.actions || [];
    const action = actions.find((a: { value: string; label: string }) => a.value === actionKey);
    if (action) {
      return action.label;
    }
  }

  return null;
};

/**
 * Get action label from status configuration (schema service).
 * This is the new API that accepts statusConfig directly instead of workflow.
 * @param actionEnum - The Action enum value
 * @param statusConfig - The status configuration from schema service
 * @param workflowType - The workflow type for action key lookup
 */
export const getActionLabelFromStatusConfig = (
  actionEnum: Action,
  statusConfig: Record<string, any> | undefined,
  workflowType?: string,
): string | null => {
  const actionKey = getActionKeyFromEnum(actionEnum, workflowType);
  if (!actionKey || !statusConfig) {
    return null;
  }

  // Search through all status configurations to find the action label
  for (const stateKey of Object.keys(statusConfig)) {
    const actions = statusConfig[stateKey]?.actions || [];
    const action = actions.find((a: { value: string; label: string }) => a.value === actionKey);
    if (action) {
      return action.label;
    }
  }

  return null;
};

// Create confirmation dialog state for an action
export const createActionDialog = (action: string): ActionDialogState | null => {
  const label = ACTION_LABELS[action];
  if (!label) {
    toast.error(`Unknown action: ${action}`);
    return null;
  }

  return {
    isOpen: true,
    action,
    label,
  };
};

// Get confirmation message for mark_as_completed action
const getMarkAsCompletedMessage = (caseState?: string): string => {
  // Show "Human Call" in message when in HUMAN_CALL state
  if (caseState === CaseState.HUMAN_CALL) {
    return 'Are you sure you want to mark Human Call as completed?';
  }
  return 'Are you sure you want to mark this case as completed?';
};

// Get confirmation message for an action
export const getActionConfirmationMessage = (action: string, caseState?: string): string => {
  // Handle special case for mark_as_completed
  if (action === 'mark_as_completed') {
    return getMarkAsCompletedMessage(caseState);
  }

  // Use predefined message or default
  return (
    ACTION_CONFIRMATION_MESSAGES[action] ||
    `Are you sure you want to ${ACTION_LABELS[action]?.toLowerCase() || 'perform this action'}?`
  );
};

// Check if an action requires call details
export const actionRequiresCallDetails = (action: string): boolean => {
  // Cancel operations work at case level, not call level
  if (action === 'cancel_case' || action === 'cancel_call') {
    return false;
  }

  // Call-specific actions require call details (prefixed with "human_call_")
  // Audit actions (mark_as_completed, retry_call, etc.) do NOT require call details
  // NOTE: These actions are workflow signals that don't require existing call records.
  // The backend generates call IDs automatically (see compass/server/http/service.go:1651)
  const callActions: string[] = [];

  return callActions.includes(action);
};

// Check if action requires note input
export const actionRequiresCallReviewNote = (action: string): boolean => {
  return action === 'call_review_completed';
};

export const actionRequiresTaskInput = (action: string): boolean => {
  return action === 'create_task' || action === 'reachout_ineligible_create_task';
};

export const actionRequiresTextMessageInput = (action: string): boolean => {
  return action === 'send_text' || action === 'reachout_ineligible_send_text';
};

export const INELIGIBILITY_TAG_FIELD_ID = 'ineligibility_tag';

export const actionRequiresIneligibilityNoteInput = (action: string): boolean => {
  return (
    action === 'mark_as_ineligible' ||
    action === 'pre_reachout_mark_as_ineligible' ||
    action === 'mark_fax_as_ineligible' ||
    action === 'pre_processing_failed_mark_as_ineligible'
  );
};

/** Only referral scheduler ineligibility actions include the tag dropdown */
export const actionRequiresIneligibilityTag = (action: string): boolean => {
  return (
    action === 'pre_reachout_mark_as_ineligible' ||
    action === 'mark_fax_as_ineligible' ||
    action === 'pre_processing_failed_mark_as_ineligible'
  );
};

export const actionRequiresUpdateIneligibilityNote = (action: string): boolean => {
  return action === 'update_ineligibility_note';
};

export const actionRequiresEmailInput = (action: string): boolean => {
  return action === 'send_email';
};

// Check if action requires a case note (not call review note).
// The irreversible close-case actions all demand a written reason: terminate, and
// both names the Cancel Case item resolves to (cancel_case, and cancel_workflow —
// what current PBM_V2_STATUS seeds name the same transition).
export const actionRequiresCaseNote = (action: string): boolean => {
  return (
    action === 'mark_as_terminated' ||
    action === 'cancel_case' ||
    action === 'cancel_workflow' ||
    // The note is the only record of the human call that resolved the case.
    action === 'refill_mark_case_as_completed' ||
    // The reason a Program Admin overrode the AI audit's loop back to the caller
    // (BV-2657). The server rejects the move without one too.
    action === 'human_call_move_to_human_audit'
  );
};

/**
 * Check if an action requires any form of user input.
 * This is useful for filtering actions in bulk operations where input cannot be collected per-action.
 */
export const actionRequiresInput = (action: string): boolean => {
  return (
    actionRequiresCallReviewNote(action) ||
    actionRequiresTaskInput(action) ||
    actionRequiresTextMessageInput(action) ||
    actionRequiresIneligibilityNoteInput(action) ||
    actionRequiresUpdateIneligibilityNote(action) ||
    actionRequiresEmailInput(action)
  );
};

// Check if modal should close after action success
export const shouldCloseModalAfterAction = (action: string): boolean => {
  // Close modal after canceling the case or marking as duplicate
  // Don't close after canceling a call as user may want to continue viewing
  return action === 'cancel_case' || action === 'mark_as_duplicate';
};

// States where cancel_case action is not allowed
const CANCEL_CASE_INVALID_STATES: string[] = [
  // Generic workflow states
  CaseState.COMPLETED,
  CaseState.TERMINATED,
  CaseState.CANCELLED,
  // Refill: the FSM reached its COMPLETED terminal to write refill_unresolved
  // (retry budget spent), so the workflow is closed and there is nothing left to
  // cancel. cancelWorkflowPrecondition rejects it server-side; without this the
  // button was offered and then 400'd.
  CaseState.REFILL_UNRESOLVED,
  // Knipper workflow states (after submission)
  KnipperCaseState.SUBMITTED,
  KnipperCaseState.ACKNOWLEDGED,
  KnipperCaseState.COMPLETED,
  KnipperCaseState.NON_REPORTABLE,
  KnipperCaseState.DUPLICATE,
  KnipperCaseState.CANCELLED,
];

// States where mark_as_duplicate action is not allowed (only allowed in intake_draft, review_needed)
const MARK_AS_DUPLICATE_INVALID_STATES: string[] = [
  KnipperCaseState.DELIVERING_EMAIL,
  KnipperCaseState.SUBMITTED,
  KnipperCaseState.ACKNOWLEDGED,
  KnipperCaseState.COMPLETED,
  KnipperCaseState.NON_REPORTABLE,
  KnipperCaseState.DUPLICATE,
  KnipperCaseState.CANCELLED,
];

// Terminal states where submit_to_manufacturer is not allowed
const SUBMIT_TO_MANUFACTURER_INVALID_STATES: string[] = [
  KnipperCaseState.COMPLETED,
  KnipperCaseState.NON_REPORTABLE,
  KnipperCaseState.DUPLICATE,
  KnipperCaseState.CANCELLED,
];

// Map of actions to their invalid states
const ACTION_INVALID_STATES: Record<string, string[]> = {
  cancel_case: CANCEL_CASE_INVALID_STATES,
  mark_as_duplicate: MARK_AS_DUPLICATE_INVALID_STATES,
  submit_to_manufacturer: SUBMIT_TO_MANUFACTURER_INVALID_STATES,
};

// Helper to check if state is an eligibility check human audit state
const isEligibilityCheckHumanAuditState = (caseState?: string): boolean => {
  if (!caseState) return false;
  return (
    caseState === CaseState.PRIMARY_ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    caseState === CaseState.SECONDARY_ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    caseState === CaseState.ELIGIBILITY_CHECK_HUMAN_AUDIT ||
    caseState.endsWith('_eligibility_check_human_audit')
  );
};

// Helper to check if case record has ack_id in output_data
const hasAckIdInOutput = (caseRecord?: GetCaseRecordResponse): boolean => {
  return !!(caseRecord?.output_data?.data as Record<string, unknown>)?.ack_id;
};

/**
 * Whether the user is a Program Admin of the case or holds a role above it (BV-2514):
 * Program Admin of the case's program, Organization Admin of its organization, or a
 * global Admin / System Admin. Matched by role slug, because Program Admin, Program
 * Manager and Supervisor grant the same permissions. Mirrors the server's
 * guardCaseProgramAdmin. Slugs: pkg/auth/role_templates.go.
 */
export const isCaseProgramAdmin = (
  roles: UserRole[] | undefined,
  organizationId: string | undefined,
  programId: string | undefined,
): boolean =>
  (roles ?? []).some(role => {
    const isGlobal = !role.organization_id && !role.program_id;
    switch (role.slug) {
      case 'admin':
      case 'system-admin':
        return isGlobal;
      case 'organization-admin':
        return !!organizationId && role.organization_id === organizationId && !role.program_id;
      case 'program-admin':
        return !!programId && role.program_id === programId;
      default:
        return false;
    }
  });

/** Workflow types whose human call has a "Move to Human Audit" action (BV-2514, BV-2657). */
const MOVE_TO_HUMAN_AUDIT_WORKFLOW_TYPES = new Set<string>([
  WorkflowType.TRANSPORTATION,
  WorkflowType.UNIFIEDMEDBV,
]);

/**
 * Whether "Move to Human Audit" is offered (BV-2514, BV-2657): a transportation or
 * unifiedmedbv case in human_call, to a Program Admin of the case or a higher role
 * only. The server enforces the same rule (guardCaseProgramAdmin), so this only
 * decides visibility.
 */
export const canMoveHumanCallToHumanAudit = (
  caseState: string | undefined,
  workflowType: string | undefined,
  isProgramAdmin: boolean,
): boolean =>
  caseState === CaseState.HUMAN_CALL &&
  !!workflowType &&
  MOVE_TO_HUMAN_AUDIT_WORKFLOW_TYPES.has(workflowType) &&
  isProgramAdmin;

/**
 * Whether a case's human audit is reserved for Program Admins and above (BV-2657): a
 * transportation or unifiedmedbv case in human_audit, the state the Program Admin's move
 * leads to. Only `human_audit` itself; ec_human_audit is not included.
 */
export const isHumanAuditForProgramAdmins = (
  caseState: string | undefined,
  workflowType: string | undefined,
): boolean =>
  caseState === CaseState.HUMAN_AUDIT &&
  !!workflowType &&
  MOVE_TO_HUMAN_AUDIT_WORKFLOW_TYPES.has(workflowType);

/**
 * Whether the user may work the case in its current state (BV-2657): start the human
 * audit, edit, save and take its audit actions. Dashboard only; the server does not
 * enforce it.
 */
export const canWorkHumanAudit = (
  caseState: string | undefined,
  workflowType: string | undefined,
  isProgramAdmin: boolean,
): boolean => !isHumanAuditForProgramAdmins(caseState, workflowType) || isProgramAdmin;

/**
 * Whether a user's role on the program is below Program Admin (BV-2657), from the roles
 * the program user search returns. That search sees program roles only, so a user with
 * none on the program is not below: an Organization Admin looks exactly like that. Used
 * to hide such users from the assignee lists of a case whose human audit is reserved for
 * Program Admins.
 */
export const isBelowProgramAdmin = (
  roles: UserRole[] | undefined,
  organizationId: string | undefined,
  programId: string | undefined,
): boolean =>
  !!programId &&
  (roles ?? []).some(role => role.program_id === programId) &&
  !isCaseProgramAdmin(roles, organizationId, programId);

// Validate if action can be performed based on case state and case data
export const canPerformAction = (
  action: string,
  caseState?: string,
  caseRecord?: GetCaseRecordResponse,
): boolean => {
  // Eligibility check actions can only be performed in eligibility check human audit states
  if (action === 'eligibility_check_retry' || action === 'eligibility_check_proceed') {
    return isEligibilityCheckHumanAuditState(caseState);
  }

  // Fail safe: mark_as_duplicate requires a known state to validate against
  if (action === 'mark_as_duplicate' && !caseState) {
    return false;
  }

  // Check if action has invalid states defined
  const invalidStates = ACTION_INVALID_STATES[action];
  if (invalidStates && caseState && invalidStates.includes(caseState)) {
    return false;
  }

  // PQC: pqc_mark_as_completed requires ack_id in output_data
  if (action === 'pqc_mark_as_completed' && !hasAckIdInOutput(caseRecord)) {
    return false;
  }

  return true;
};

// Execute case action handler
export const executeCaseAction = (
  action: string,
  caseId: string,
  workflowType: string | undefined,
  executeActionMutate: (params: ExecuteActionParams, callbacks?: ExecuteActionCallbacks) => void,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (error: any) => void;
    onCloseDialog?: () => void;
    onCloseModal?: () => void;
  },
  actionDataParams?: ActionDataParams,
  options?: { skipToast?: boolean; caseState?: string },
) => {
  const actionEnum = getActionEnum(action, workflowType, options?.caseState);

  if (!actionEnum) {
    toast.error(`Action not configured: ${action}`);
    return;
  }

  const shouldCloseModal = shouldCloseModalAfterAction(action);

  executeActionMutate(
    {
      caseId,
      action: actionEnum,
      actionDataParams,
      skipToast: options?.skipToast,
    },
    {
      onSuccess: () => {
        callbacks?.onCloseDialog?.();
        callbacks?.onSuccess?.();

        if (shouldCloseModal) {
          callbacks?.onCloseModal?.();
        }
      },
      onError: error => {
        // Keep dialog open on error to allow retry
        callbacks?.onError?.(error);
      },
    },
  );
};

export const handleCancelCallAction = (
  caseId: string,
  executeActionMutate: (params: ExecuteActionParams, callbacks?: ExecuteActionCallbacks) => void,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (error: any) => void;
    onCloseDialog?: () => void;
  },
  actionDataParams?: ActionDataParams,
) => {
  const actionEnum = Action.ACTION_CANCEL_CALL;

  if (!actionEnum) {
    toast.error('Cancel call action not configured');
    return;
  }

  executeActionMutate(
    {
      caseId,
      action: actionEnum,
      actionDataParams,
      skipToast: true, // Skip the default toast
    },
    {
      onSuccess: () => {
        callbacks?.onCloseDialog?.();
        callbacks?.onSuccess?.();
        toast.success('Call ended successfully');
      },
      onError: error => {
        callbacks?.onError?.(error);
      },
    },
  );
};

export const handleCancelCaseAction = (
  caseId: string,
  executeActionMutate: (params: ExecuteActionParams, callbacks?: ExecuteActionCallbacks) => void,
  callbacks?: {
    onSuccess?: () => void;
    onError?: (error: any) => void;
    onCloseDialog?: () => void;
    onCloseModal?: () => void;
  },
  actionDataParams?: ActionDataParams,
) => {
  const actionEnum = Action.ACTION_CANCEL_WORKFLOW;

  if (!actionEnum) {
    toast.error('Cancel workflow action not configured');
    return;
  }

  executeActionMutate(
    {
      caseId,
      action: actionEnum,
      actionDataParams,
    },
    {
      onSuccess: () => {
        callbacks?.onCloseDialog?.();
        callbacks?.onSuccess?.();
        callbacks?.onCloseModal?.(); // Always close modal after canceling
      },
      onError: error => {
        callbacks?.onError?.(error);
      },
    },
  );
};

// Get available actions for a case state from workflow configuration
export const getAvailableActionsForCaseState = (
  workflow: any,
  caseState: string,
): Array<{ value: string; label: string }> => {
  if (!workflow?.ui || !caseState) {
    return [];
  }

  const uiData = workflow.ui as any;
  const statusConfig = uiData.status;
  if (!statusConfig) {
    return [];
  }

  return getAvailableActionsFromStatusConfig(statusConfig, caseState);
};

/**
 * Get available actions for a case state from status configuration (schema service).
 * This is the new API that accepts statusConfig directly instead of workflow.
 * @param statusConfig - The status configuration from schema service
 * @param caseState - The current case state
 */
export const getAvailableActionsFromStatusConfig = (
  statusConfig: Record<string, any> | undefined,
  caseState: string,
): Array<{ value: string; label: string }> => {
  if (!statusConfig || !caseState) {
    return [];
  }

  const currentStatus = statusConfig[caseState];
  let actions = currentStatus?.actions || [];

  // TODO: Remove terminated action from human call in db and don't return mark_as_terminated action if state is human_call
  if (caseState === CaseState.HUMAN_CALL) {
    actions = actions.filter((action: any) => action.value !== 'mark_as_terminated');
  }

  // Return actions with both value and label
  return actions;
};

// Helper to categorize a single action
const categorizeAction = (
  action: ActionItem,
  categorizedActionsMap: Map<string | null, ActionItem[]>,
  groupCategories: Map<string, ActionItem[]>,
): void => {
  if (!action) return;

  // Check if action matches predefined categories
  for (const [categoryLabel, actions] of ACTION_CATEGORIES.entries()) {
    if (actions.includes(action.value)) {
      categorizedActionsMap.get(categoryLabel)?.push(action);
      return;
    }
  }

  // Use group-based categorization if has group field
  if (action.group) {
    if (!groupCategories.has(action.group)) {
      groupCategories.set(action.group, []);
    }
    groupCategories.get(action.group)?.push(action);
    return;
  }

  // Add to 'Other Actions' category
  const otherCategory = 'Other Actions';
  if (!categorizedActionsMap.has(otherCategory)) {
    categorizedActionsMap.set(otherCategory, []);
  }
  categorizedActionsMap.get(otherCategory)?.push(action);
};

// "Close case" is a terminal group (mark completed / terminated) that must always
// render LAST — after every other heading, including "Other Actions".
const CLOSE_CASE_CATEGORY = 'Close case';

// Helper to build result from categorized actions
const buildCategorizedResult = (
  categorizedActionsMap: Map<string | null, ActionItem[]>,
  groupCategories: Map<string, ActionItem[]>,
): CategorizedActionsResult => {
  const result: CategorizedActionsResult = [];

  // Add predefined categories (key is both category and label), except
  // "Close case" which is appended last below.
  ACTION_CATEGORIES.forEach((_actions, categoryLabel) => {
    if (categoryLabel === CLOSE_CASE_CATEGORY) return;
    const categoryActions = categorizedActionsMap.get(categoryLabel);
    if (categoryActions?.length) {
      result.push({
        category: categoryLabel || '',
        categoryLabel: categoryLabel || '',
        actions: categoryActions,
      });
    }
  });

  // Add group-based categories (sorted, key is both category and label)
  Array.from(groupCategories.keys())
    .sort()
    .forEach(groupLabel => {
      const groupActions = groupCategories.get(groupLabel);
      if (groupActions?.length) {
        result.push({ category: groupLabel, categoryLabel: groupLabel, actions: groupActions });
      }
    });

  // Add 'Other Actions' category
  const otherCategory = 'Other Actions';
  const otherActions = categorizedActionsMap.get(otherCategory);
  if (otherActions?.length) {
    result.push({ category: otherCategory, categoryLabel: otherCategory, actions: otherActions });
  }

  // "Close case" is always last — after Other Actions.
  const closeCaseActions = categorizedActionsMap.get(CLOSE_CASE_CATEGORY);
  if (closeCaseActions?.length) {
    result.push({
      category: CLOSE_CASE_CATEGORY,
      categoryLabel: CLOSE_CASE_CATEGORY,
      actions: closeCaseActions,
    });
  }

  return result;
};

// Categorize actions dynamically based on configuration
export const categorizeActions = (actions: ActionItem[]): CategorizedActionsResult => {
  const categorizedActionsMap = new Map<string | null, ActionItem[]>();
  const groupCategories = new Map<string, ActionItem[]>();

  // Initialize predefined categories (using label as key)
  ACTION_CATEGORIES.forEach((_actions, categoryLabel) => {
    categorizedActionsMap.set(categoryLabel, []);
  });

  // Categorize each action
  actions.forEach(action => categorizeAction(action, categorizedActionsMap, groupCategories));

  // Build and return result
  return buildCategorizedResult(categorizedActionsMap, groupCategories);
};

// Transform action for human call states (matches CaseActionsDropdown logic)
export const transformActionForHumanCall = (action: string, caseState?: string): string => {
  const isInHumanCallState = caseState === CaseState.HUMAN_CALL;

  // Convert mark_as_completed to human_call_mark_as_completed when in human call state
  if (isInHumanCallState && action === 'mark_as_completed') {
    return 'human_call_mark_as_completed';
  }

  return action;
};

// Get display label for action (matches CaseActionsDropdown logic)
export const getActionLabel = (action: string, caseState?: string): string => {
  const isInHumanCallState = caseState === CaseState.HUMAN_CALL;

  // Special case for mark_as_completed in human call state
  if (isInHumanCallState && action === 'mark_as_completed') {
    return 'Mark Human Call as Completed';
  }

  return ACTION_LABELS[action] || action;
};

// Actions whose dashboard label intentionally overrides the workflow's
// config-provided label, so the copy is controlled in the UI (ACTION_LABELS /
// getActionLabel) rather than per-program status config.
const LABEL_OVERRIDE_ACTIONS = new Set(['mark_as_completed']);

/**
 * Resolve the display label for a dropdown action: the workflow config label by
 * default, but the UI override (getActionLabel) for LABEL_OVERRIDE_ACTIONS.
 */
export const resolveActionLabel = (
  action: { value: string; label?: string },
  caseState?: string,
): string => {
  if (LABEL_OVERRIDE_ACTIONS.has(action.value)) {
    return getActionLabel(action.value, caseState);
  }
  return action.label || getActionLabel(action.value, caseState);
};

/**
 * Resolve the display label for a category heading in the actions dropdown.
 *
 * In the human-call state the terminal "Close case" group completes the human
 * call (its item reads "Mark Human Call as Completed", see getActionLabel), not
 * the whole case — so the heading reads "Close call". Keyed on case state, not
 * workflow, so it applies across all workflows.
 */
export const resolveCategoryLabel = (categoryLabel: string, caseState?: string): string => {
  if (caseState === CaseState.HUMAN_CALL && categoryLabel === CLOSE_CASE_CATEGORY) {
    return 'Close call';
  }
  return categoryLabel;
};

// Mapping of actions to their state transitions (from -> to)
// Used for displaying state change notes
// Keys are the full action enum names as stored by the backend (e.g., ACTION_BV_AUDIT_CALL_WITH_AI)
// Values are status keys (IDs) that can be used with getStatusColors to get display labels and colors
const ACTION_STATE_TRANSITIONS: Record<string, { from: string; to: string }> = {
  // Benefit Verification (BV) workflow actions
  ACTION_BV_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_BV_AUDIT_CALL_WITH_HUMAN: { from: 'human_audit', to: 'human_call' },
  ACTION_BV_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_BV_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },

  // PBM (Pharmacy Benefit Manager) workflow actions
  ACTION_PBM_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_PBM_AUDIT_CALL_WITH_HUMAN: { from: 'human_audit', to: 'human_call' },
  ACTION_PBM_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_PBM_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },
  ACTION_PBM_AUDIT_CANCEL_AI_AUDIT: { from: 'ai_audit', to: 'human_audit' },
  // Generic cancel-AI-audit (BV-2131). Display-only in NotesPanel; the case may be
  // in any AI-audit state (ai_audit / ai_call_audit / human_call_ai_audit) but a
  // single label suffices for the note header.
  ACTION_CANCEL_AI_AUDIT: { from: 'ai_audit', to: 'human_audit' },

  // MedBV (Medical Benefit Verification) workflow actions
  ACTION_MEDBV_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_MEDBV_AUDIT_CALL_WITH_HUMAN: { from: 'human_audit', to: 'human_call' },
  ACTION_MEDBV_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_MEDBV_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },
  ACTION_MEDBV_ELIGIBILITY_CHECK_RETRY: {
    from: 'eligibility_check_human_audit',
    to: 'eligibility_check',
  },
  ACTION_MEDBV_ELIGIBILITY_CHECK_PROCEED: {
    from: 'eligibility_check_human_audit',
    to: 'ai_call',
  },

  // Recall Scheduler workflow actions
  ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_HUMAN: { from: 'human_audit', to: 'human_call' },
  ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },
  ACTION_RECALL_SCHEDULER_AUDIT_UPDATE_NOTE: { from: 'human_audit', to: 'update_note' },
  ACTION_RECALL_SCHEDULER_CALL_REVIEW_COMPLETED: { from: 'call_review', to: 'human_audit' },
  ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_APPROVED: {
    from: 'case_completion_review',
    to: 'completed',
  },
  ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_NEEDS_REVISION: {
    from: 'case_completion_review',
    to: 'update_note',
  },
  ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_REJECTED: {
    from: 'case_completion_review',
    to: 'human_audit',
  },
  ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_APPROVED: {
    from: 'ineligible_case_completion_review',
    to: 'completed',
  },
  ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_REJECTED: {
    from: 'ineligible_case_completion_review',
    to: 'human_audit',
  },

  // Medicare Human workflow actions
  ACTION_MEDICARE_HUMAN_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_MEDICARE_HUMAN_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },
  ACTION_MEDICARE_HUMAN_AUDIT_RETRY_CALL: { from: 'human_audit', to: 'retry_call' },
  ACTION_MEDICARE_HUMAN_CALL_MARK_AS_COMPLETED: { from: 'human_call', to: 'human_audit' },
  ACTION_MEDICARE_HUMAN_CALL_MARK_AS_FAILED: { from: 'human_call', to: 'failed' },
  ACTION_MEDICARE_HUMAN_ELIGIBILITY_CHECK_RETRY: {
    from: 'eligibility_check_human_audit',
    to: 'eligibility_check',
  },
  ACTION_MEDICARE_HUMAN_ELIGIBILITY_CHECK_PROCEED: {
    from: 'eligibility_check_human_audit',
    to: 'human_audit',
  },

  // Referral Scheduler workflow actions
  ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_HUMAN: { from: 'human_audit', to: 'human_call' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_MARK_AS_COMPLETED: { from: 'human_audit', to: 'completed' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_MARK_AS_TERMINATED: { from: 'human_audit', to: 'terminated' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_UPDATE_NOTE: { from: 'human_audit', to: 'update_note' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_CREATE_TASK: { from: 'human_audit', to: 'human_audit' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_SEND_TEXT: { from: 'human_audit', to: 'human_audit' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_SEND_EMAIL: { from: 'human_audit', to: 'human_audit' },
  ACTION_REFERRAL_SCHEDULER_AUDIT_CLOSE_REFERRAL_FAX: { from: 'human_audit', to: 'human_audit' },
  // Referral Scheduler fax verification actions
  ACTION_REFERRAL_SCHEDULER_FAX_VERIFICATION_MARK_FAX_AS_INELIGIBLE: {
    from: 'fax_verification',
    to: 'fax_ineligible',
  },
  ACTION_REFERRAL_SCHEDULER_FAX_VERIFICATION_CONFIRM_AND_CONTINUE: {
    from: 'fax_verification',
    to: 'pre_call_data_fetch',
  },
  // Referral Scheduler fax ineligible actions
  ACTION_REFERRAL_SCHEDULER_FAX_INELIGIBLE_MARK_FAX_AS_ELIGIBLE: {
    from: 'fax_ineligible',
    to: 'fax_verification',
  },
  ACTION_REFERRAL_SCHEDULER_FAX_INELIGIBLE_MARK_AS_TERMINATED: {
    from: 'fax_ineligible',
    to: 'terminated',
  },
  // Referral Scheduler case completion review actions
  ACTION_REFERRAL_SCHEDULER_CASE_COMPLETION_REVIEW_APPROVED: {
    from: 'case_completion_review',
    to: 'completed',
  },
  ACTION_REFERRAL_SCHEDULER_CASE_COMPLETION_REVIEW_NEEDS_REVISION: {
    from: 'case_completion_review',
    to: 'human_audit',
  },
  ACTION_REFERRAL_SCHEDULER_CASE_COMPLETION_REVIEW_REJECTED: {
    from: 'case_completion_review',
    to: 'human_audit',
  },
  // Referral Scheduler call review actions
  ACTION_REFERRAL_SCHEDULER_CALL_REVIEW_COMPLETED: { from: 'call_review', to: 'human_audit' },
  ACTION_REFERRAL_SCHEDULER_CALL_REVIEW_REJECTED: { from: 'call_review', to: 'human_audit' },
  // Referral Scheduler ineligible actions
  ACTION_REFERRAL_SCHEDULER_INELIGIBLE_MARK_AS_ELIGIBLE: {
    from: 'ineligible',
    to: 'pre_reachout_review',
  },
  ACTION_REFERRAL_SCHEDULER_INELIGIBLE_MARK_AS_TERMINATED: {
    from: 'ineligible',
    to: 'terminated',
  },
  // Referral Scheduler pre-processing failed actions
  ACTION_REFERRAL_SCHEDULER_PRE_PROCESSING_FAILED_CONFIRM_AND_CONTINUE: {
    from: 'pre_processing_failed',
    to: 'ai_call',
  },
  ACTION_REFERRAL_SCHEDULER_PRE_PROCESSING_FAILED_MARK_AS_INELIGIBLE: {
    from: 'pre_processing_failed',
    to: 'ineligible',
  },
  // Referral Scheduler missing input failure actions
  ACTION_REFERRAL_SCHEDULER_MISSING_INPUT_COMPLETED: {
    from: 'missing_input_failure',
    to: 'created',
  },
  // Referral Scheduler pre-reachout review actions
  ACTION_REFERRAL_SCHEDULER_PRE_REACHOUT_REVIEW_APPROVE_FOR_CALL: {
    from: 'pre_reachout_review',
    to: 'ai_call',
  },
  ACTION_REFERRAL_SCHEDULER_PRE_REACHOUT_REVIEW_MARK_AS_INELIGIBLE: {
    from: 'pre_reachout_review',
    to: 'ineligible',
  },

  // PA-Status PBM / MedBV audit actions (BI-1658)
  ACTION_PA_STATUS_PBM_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },
  ACTION_PA_STATUS_MEDBV_AUDIT_CALL_WITH_AI: { from: 'human_audit', to: 'ai_call' },

  // Common actions applicable to all workflows
  ACTION_HUMAN_CALL_MARK_AS_COMPLETED: { from: 'human_call', to: 'human_audit' },
  ACTION_HUMAN_CALL_MARK_AS_TERMINATED: { from: 'human_call', to: 'terminated' },
  // Program Admin's moves (BV-2514, BV-2657) — namespaced, so the transition is fixed.
  ACTION_TRANSPORTATION_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT: { from: 'human_call', to: 'human_audit' },
  ACTION_UNIFIEDMEDBV_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT: { from: 'human_call', to: 'human_audit' },
  ACTION_CANCEL_CALL: { from: 'ai_call', to: 'human_audit' },
  ACTION_CANCEL_WORKFLOW: { from: 'human_audit', to: 'cancelled' },

  // Refill (JNJ-109). The one refill cancel whose transition is FIXED: its
  // precondition allows only refill_scheduled and the FSM's cancelCase always
  // writes terminated. Needs no workflow-type branch below — the action is
  // namespaced to refill, so it can only ever arrive on a refill case.
  ACTION_REFILL_CANCEL_SCHEDULED_CALL: { from: 'refill_scheduled', to: 'terminated' },
  // Fixed too: allowed only from human_call_needed, and the FSM always completes.
  ACTION_REFILL_MARK_CASE_AS_COMPLETED: { from: 'human_call_needed', to: 'completed' },
};

/**
 * Refill cancels with NO fixed transition — the note falls back to the action label
 * rather than rendering status badges for a change the case did not make.
 *
 * ACTION_CANCEL_CALL hangs up the in-flight call and rejoins the normal
 * inconclusive path, landing on refill_scheduled while the retry budget allows and
 * refill_unresolved once it is spent; the destination depends on attempts already
 * burned, which a static map cannot know. ACTION_CANCEL_WORKFLOW is delivered by a
 * stateflow GLOBAL signal, so it fires from any live state and always writes
 * `terminated` (not the shared entry's `cancelled`) — destination fixed, origin not,
 * and the badges need both.
 */
const REFILL_UNFIXED_TRANSITION_ACTIONS = new Set<Action>([
  Action.ACTION_CANCEL_CALL,
  Action.ACTION_CANCEL_WORKFLOW,
]);

// Get state transition for an action (used for displaying state change notes)
// Some actions have different transitions depending on workflow type
// Returns null if the action is not found in the mapping
export const getStateTransition = (
  action: Action,
  workflowType?: string,
): { from: string; to: string } | null => {
  // PQC workflow: Cancel and mark-as-duplicate actions can come from multiple states
  // (intake_draft or review_needed), so we can't show a fixed "from" state.
  // Return null to fall back to showing just the action label.
  if (workflowType === WorkflowType.PQC) {
    if (
      action === Action.ACTION_CANCEL_WORKFLOW ||
      action === Action.ACTION_PQC_MARK_AS_DUPLICATE
    ) {
      return null;
    }
  }

  // Handle workflow-specific transitions
  if (
    action === Action.ACTION_HUMAN_CALL_MARK_AS_COMPLETED &&
    workflowType === WorkflowType.RECALL_SCHEDULER
  ) {
    return { from: 'human_call', to: 'call_review' };
  }

  // Refill (JNJ-109): see REFILL_UNFIXED_TRANSITION_ACTIONS for why these two have
  // no fixed transition. Same label-only fallback PQC takes above.
  if (workflowType === WorkflowType.REFILL && REFILL_UNFIXED_TRANSITION_ACTIONS.has(action)) {
    return null;
  }

  // Convert numeric Action enum to string name for lookup in ACTION_STATE_TRANSITIONS
  const actionName = Action[action];
  return ACTION_STATE_TRANSITIONS[actionName] || null;
};

// ============================================================================
// Action Data Builders
// These functions build the action_data payload for executeAction API calls
// ============================================================================

/**
 * Check if the action is an audit decision action (mark as completed/terminated)
 */
export const isAuditDecisionAction = (action: Action): boolean => {
  return (
    action === Action.ACTION_BV_AUDIT_MARK_AS_COMPLETED ||
    action === Action.ACTION_BV_AUDIT_MARK_AS_TERMINATED
  );
};

/**
 * Check if the action is a human call action
 */
export const isHumanCallAction = (action: Action): boolean => {
  return (
    action === Action.ACTION_BV_AUDIT_CALL_WITH_AI ||
    action === Action.ACTION_BV_AUDIT_CALL_WITH_HUMAN
  );
};

/**
 * Terminal human-audit actions that route the case back to a human caller. These
 * are the only actions for which a human auditor may attach handoff actions for the
 * caller; mirrors the backend `actionRoutesToHumanCall`.
 */
/**
 * The same set keyed by ACTION NAME (what StatusDropdown / the confirmation dialog pass
 * around) rather than the resolved `Action` enum. `call_with_human` maps to
 * `ACTION_<WF>_AUDIT_CALL_WITH_HUMAN` and `audit_move_to_human_call` to
 * `ACTION_<WF>_AUDIT_MOVE_TO_HUMAN_CALL` (see `getWorkflowActionMap`). Note the
 * similarly-named `move_to_human_call` is the ELIGIBILITY-CHECK action
 * (`..._EC_MOVE_TO_HUMAN_CALL`) and is deliberately NOT in this set.
 */
const AUDIT_ROUTE_TO_HUMAN_CALL_ACTION_NAMES: readonly string[] = [
  'call_with_human',
  'audit_move_to_human_call',
];

/** True when an action NAME routes an audit back to a human caller. */
export const actionNameRoutesToHumanCall = (actionName: string): boolean =>
  AUDIT_ROUTE_TO_HUMAN_CALL_ACTION_NAMES.includes(actionName);

const actionRoutesToHumanCall = (action: Action): boolean => {
  return (
    action === Action.ACTION_PBM_AUDIT_CALL_WITH_HUMAN ||
    action === Action.ACTION_UNIFIEDMEDBV_AUDIT_MOVE_TO_HUMAN_CALL ||
    action === Action.ACTION_BHO_AUDIT_MOVE_TO_HUMAN_CALL ||
    action === Action.ACTION_TRANSPORTATION_AUDIT_CALL_WITH_HUMAN
  );
};

/** Actions that support an optional scheduled_at timestamp (call_with_ai/call_with_human across all workflows) */
const SCHEDULABLE_ACTION_SET = new Set<Action>([
  Action.ACTION_BV_AUDIT_CALL_WITH_AI,
  Action.ACTION_BV_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_PBM_AUDIT_CALL_WITH_AI,
  Action.ACTION_PBM_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_MEDBV_AUDIT_CALL_WITH_AI,
  Action.ACTION_MEDBV_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_AI,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_AI,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_REINITIATE_AI_CALL,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_MOVE_TO_HUMAN_CALL,
  Action.ACTION_BHO_AUDIT_REINITIATE_AI_CALL,
  Action.ACTION_BHO_AUDIT_MOVE_TO_HUMAN_CALL,
]);

export const isSchedulableAction = (action: Action): boolean => SCHEDULABLE_ACTION_SET.has(action);

/** Action name strings (workflow-agnostic) that support scheduling. Used by UI components. */
export const SCHEDULABLE_ACTION_NAMES = ['call_with_ai', 'call_with_human'] as const;

/**
 * Check if the action is a call review action
 */
export const isCallReviewAction = (action: Action): boolean => {
  return (
    action === Action.ACTION_RECALL_SCHEDULER_CALL_REVIEW_COMPLETED ||
    action === Action.ACTION_RECALL_SCHEDULER_CALL_REVIEW_FAILED ||
    action === Action.ACTION_REFERRAL_SCHEDULER_CALL_REVIEW_COMPLETED ||
    action === Action.ACTION_REFERRAL_SCHEDULER_CALL_REVIEW_REJECTED
  );
};

/**
 * Build custom action data for specific actions (create task, send text, send email, etc.)
 */
// Actions that use task_id
const taskIdActions: Set<Action> = new Set([
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CREATE_TASK,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_CREATE_TASK,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_UPDATE_INELIGIBILITY_NOTE,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_CREATE_TASK,
]);

// Actions that use text_id
const textIdActions: Set<Action> = new Set([
  Action.ACTION_RECALL_SCHEDULER_AUDIT_SEND_TEXT,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_SEND_TEXT,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_SEND_TEXT,
]);

// Helper functions to build action data for each type
const buildTaskIdActionData = (params?: ActionDataParams): ActionData => ({
  audit_decision: { task_id: params?.task_id },
});

const buildTextIdActionData = (params?: ActionDataParams): ActionData => ({
  audit_decision: { text_id: params?.text_id },
});

const buildEmailIdActionData = (params?: ActionDataParams): ActionData => ({
  audit_decision: { email_id: params?.email_id },
});

const buildIneligibilityNoteActionData = (params?: ActionDataParams): ActionData => ({
  audit_decision: {
    ineligibility_note: params?.ineligibility_note,
    ineligibility_tag: params?.ineligibility_tag,
  },
});

const buildAttachToCaseActionData = (params?: ActionDataParams): ActionData => ({
  attach_to_case: { call_id: params?.call_id },
});

/** Actions that use email_id action data */
const emailIdActions = new Set<Action>([
  Action.ACTION_RECALL_SCHEDULER_AUDIT_SEND_EMAIL,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_SEND_EMAIL,
]);

/** Actions that use ineligibility_note action data */
const ineligibilityNoteActions = new Set<Action>([
  Action.ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_INELIGIBLE,
  Action.ACTION_REFERRAL_SCHEDULER_PRE_REACHOUT_REVIEW_MARK_AS_INELIGIBLE,
  Action.ACTION_REFERRAL_SCHEDULER_FAX_VERIFICATION_MARK_FAX_AS_INELIGIBLE,
  Action.ACTION_REFERRAL_SCHEDULER_PRE_PROCESSING_FAILED_MARK_AS_INELIGIBLE,
]);

/** Build portal session action data with task_id */
const buildPortalSessionActionData = (params?: ActionDataParams): ActionData | undefined =>
  params?.task_id ? { start_portal_session: { task_id: params.task_id } } : undefined;

/** Build PQC output field update action data */
const buildUpdateOutputActionData = (params?: ActionDataParams): ActionData | undefined => ({
  update_output: { fields: params?.output_fields || {} },
});

/** Actions mapped to a specific single-action builder */
/**
 * Builds ActionData for ACTION_REFILL_CANCEL_SCHEDULED_CALL with an optional reason.
 *
 * Reuses CancelCallData because the backend reads the reason off it for this action
 * — the payload is a bare reason string, so a third near-identical message would
 * earn nothing. Without this the FSM only ever sees its own default reason, and the
 * operator's text would survive as a note but never reach the signal.
 */
const buildRefillCancelScheduledCallActionData = (
  actionDataParams?: ActionDataParams,
): ActionData | undefined =>
  actionDataParams?.cancelReason
    ? { cancel_call: { reason: actionDataParams.cancelReason } }
    : undefined;

const singleActionBuilders = new Map<Action, (params?: ActionDataParams) => ActionData | undefined>(
  [
    [Action.ACTION_PQC_ATTACH_TO_CASE, buildAttachToCaseActionData],
    [Action.ACTION_PQC_START_PORTAL_SESSION, buildPortalSessionActionData],
    [Action.ACTION_PQC_UPDATE_OUTPUT, buildUpdateOutputActionData],
    [Action.ACTION_REFILL_CANCEL_SCHEDULED_CALL, buildRefillCancelScheduledCallActionData],
  ],
);

export const buildCustomActionData = (
  action: Action,
  actionDataParams?: ActionDataParams,
): ActionData | undefined => {
  if (taskIdActions.has(action)) return buildTaskIdActionData(actionDataParams);
  if (textIdActions.has(action)) return buildTextIdActionData(actionDataParams);
  if (emailIdActions.has(action)) return buildEmailIdActionData(actionDataParams);
  if (ineligibilityNoteActions.has(action))
    return buildIneligibilityNoteActionData(actionDataParams);
  return singleActionBuilders.get(action)?.(actionDataParams);
};

/**
 * Build the action_data payload for the executeAction API call.
 * This transforms ActionDataParams into the ActionData format expected by the API.
 */
/** Builds ActionData for ACTION_CANCEL_WORKFLOW with optional reason. */
const buildCancelWorkflowActionData = (
  actionDataParams?: ActionDataParams,
): ActionData | undefined =>
  actionDataParams?.cancelReason
    ? { cancel_workflow: { reason: actionDataParams.cancelReason } }
    : undefined;

/** Builds ActionData for predefined action categories (audit/human-call/call-review). */
const buildCategorizedActionData = (
  action: Action,
  actionDataParams?: ActionDataParams,
): ActionData | undefined => {
  if (isAuditDecisionAction(action)) {
    return {
      audit_decision: {
        reason: actionDataParams?.reason,
        task_id: actionDataParams?.task_id,
      },
    };
  }
  if (isHumanCallAction(action)) {
    return { human_call: {} };
  }
  if (isCallReviewAction(action)) {
    return { call_review: { note: actionDataParams?.note } };
  }
  return undefined;
};

/**
 * Route-to-human-call actions carry optional handoff actions for the caller.
 * Returns audit_decision data only when at least one non-empty handoff row is
 * present; otherwise undefined so the action executes with no action_data.
 */
const buildHandoffActionData = (
  action: Action,
  actionDataParams?: ActionDataParams,
): ActionData | undefined => {
  if (!actionRoutesToHumanCall(action) || !actionDataParams?.handoff_actions?.length) {
    return undefined;
  }
  const handoffActions = actionDataParams.handoff_actions.filter(
    h => (h.field ?? '').trim() !== '' || (h.note ?? '').trim() !== '',
  );
  return handoffActions.length > 0
    ? { audit_decision: { handoff_actions: handoffActions } }
    : undefined;
};

export const buildActionData = (
  action: Action,
  actionDataParams?: ActionDataParams,
): ActionData | undefined => {
  // Schedulable actions with scheduled_at — return audit_decision with timestamp
  if (actionDataParams?.scheduled_at && isSchedulableAction(action)) {
    return { audit_decision: { scheduled_at: actionDataParams.scheduled_at } };
  }

  const handoff = buildHandoffActionData(action, actionDataParams);
  if (handoff !== undefined) {
    return handoff;
  }

  // Cancel workflow with optional reason
  if (action === Action.ACTION_CANCEL_WORKFLOW) {
    return buildCancelWorkflowActionData(actionDataParams);
  }

  // Predefined action categories (audit/human-call/call-review)
  const categorized = buildCategorizedActionData(action, actionDataParams);
  if (categorized !== undefined) {
    return categorized;
  }

  // Custom action data (create task, send text, send email, mark as ineligible, etc.)
  return buildCustomActionData(action, actionDataParams);
};

// ============================================================================
// Action-to-Endpoint Routing
// Maps each Action enum value to the correct task-type-specific service method.
// This mirrors the backend action whitelist maps in service/case_records.go.
// ============================================================================

type ExecuteActionFn = (
  request: ExecuteActionRequest,
  options?: ServiceOptions,
) => Promise<ExecuteActionResponse>;

/** Actions that belong to the audit endpoint */
const AUDIT_ACTIONS = new Set<Action>([
  Action.ACTION_BV_AUDIT_CALL_WITH_AI,
  Action.ACTION_BV_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_BV_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_BV_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_PBM_AUDIT_CALL_WITH_AI,
  Action.ACTION_PBM_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_PBM_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_PBM_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_PBM_AUDIT_CANCEL_AI_AUDIT,
  Action.ACTION_CANCEL_AI_AUDIT,
  Action.ACTION_MEDBV_AUDIT_CALL_WITH_AI,
  Action.ACTION_MEDBV_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_MEDBV_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_MEDBV_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_AI,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_MEDICARE_HUMAN_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_MEDICARE_HUMAN_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_MEDICARE_HUMAN_AUDIT_RETRY_CALL,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_AI,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_CALL_WITH_HUMAN,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_REFERRAL_SCHEDULER_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_UPDATE_NOTE,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_CREATE_TASK,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_SEND_TEXT,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_CREATE_TASK,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_SEND_TEXT,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_UPDATE_INELIGIBILITY_NOTE,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_MARK_AS_ELIGIBLE,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_MARK_AS_INELIGIBLE,
  Action.ACTION_RECALL_SCHEDULER_REACHOUT_INELIGIBLE_MARK_AS_TERMINATED,
  Action.ACTION_RECALL_SCHEDULER_AUDIT_SEND_EMAIL,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_REINITIATE_AI_CALL,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_MOVE_TO_HUMAN_CALL,
  Action.ACTION_UNIFIEDMEDBV_AUDIT_MOVE_TO_MEDICARE,
  Action.ACTION_BHO_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_BHO_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_BHO_AUDIT_REINITIATE_AI_CALL,
  Action.ACTION_BHO_AUDIT_MOVE_TO_HUMAN_CALL,
  Action.ACTION_BHO_AUDIT_MOVE_TO_MEDICARE,
  Action.ACTION_PA_STATUS_PBM_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_PA_STATUS_PBM_AUDIT_MOVE_TO_HUMAN_CALL,
  Action.ACTION_PA_STATUS_PBM_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_PA_STATUS_PBM_AUDIT_CALL_WITH_AI,
  Action.ACTION_PA_STATUS_MEDBV_AUDIT_MARK_AS_COMPLETED,
  Action.ACTION_PA_STATUS_MEDBV_AUDIT_MOVE_TO_HUMAN_CALL,
  Action.ACTION_PA_STATUS_MEDBV_AUDIT_MARK_AS_TERMINATED,
  Action.ACTION_PA_STATUS_MEDBV_AUDIT_CALL_WITH_AI,
]);

/** Actions that belong to the call endpoint */
const CALL_ACTIONS = new Set<Action>([
  Action.ACTION_HUMAN_CALL_MARK_AS_COMPLETED,
  Action.ACTION_HUMAN_CALL_MARK_AS_TERMINATED,
  Action.ACTION_MEDICARE_HUMAN_CALL_MARK_AS_COMPLETED,
  Action.ACTION_MEDICARE_HUMAN_CALL_MARK_AS_FAILED,
  Action.ACTION_TRANSPORTATION_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT,
  Action.ACTION_UNIFIEDMEDBV_HUMAN_CALL_MOVE_TO_HUMAN_AUDIT,
]);

/** Actions that belong to the eligibility-check-audit endpoint */
const ELIGIBILITY_CHECK_AUDIT_ACTIONS = new Set<Action>([
  Action.ACTION_MEDICARE_HUMAN_ELIGIBILITY_CHECK_RETRY,
  Action.ACTION_MEDICARE_HUMAN_ELIGIBILITY_CHECK_PROCEED,
  Action.ACTION_MEDBV_ELIGIBILITY_CHECK_RETRY,
  Action.ACTION_MEDBV_ELIGIBILITY_CHECK_PROCEED,
  Action.ACTION_UNIFIEDMEDBV_EC_REINITIATE_EC,
  Action.ACTION_UNIFIEDMEDBV_EC_MOVE_TO_AI_CALL,
  Action.ACTION_UNIFIEDMEDBV_EC_MOVE_TO_HUMAN_CALL,
  Action.ACTION_UNIFIEDMEDBV_EC_MOVE_TO_MEDICARE,
  Action.ACTION_UNIFIEDMEDBV_EC_COMPLETE,
  Action.ACTION_UNIFIEDMEDBV_EC_TERMINATE,
]);

/** Actions that belong to the intake-draft endpoint */
const INTAKE_DRAFT_ACTIONS = new Set<Action>([Action.ACTION_PQC_SUBMIT_FOR_REVIEW]);

/** Actions that belong to the review endpoint */
const REVIEW_ACTIONS = new Set<Action>([
  Action.ACTION_PQC_SUBMIT_TO_MANUFACTURER,
  Action.ACTION_RECALL_SCHEDULER_CALL_REVIEW_COMPLETED,
  Action.ACTION_RECALL_SCHEDULER_CALL_REVIEW_FAILED,
  Action.ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_APPROVED,
  Action.ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_NEEDS_REVISION,
  Action.ACTION_RECALL_SCHEDULER_CASE_COMPLETION_REVIEW_REJECTED,
  Action.ACTION_MEDICARE_HUMAN_MARK_CASE_COMPLETED,
  Action.ACTION_PQC_MARK_AS_COMPLETED,
  Action.ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_APPROVED,
  Action.ACTION_RECALL_SCHEDULER_INELIGIBLE_CASE_COMPLETION_REVIEW_REJECTED,
  Action.ACTION_PQC_MARK_AS_DUPLICATE,
]);

/** Actions that belong to the resend-email endpoint */
const RESEND_EMAIL_ACTIONS = new Set<Action>([Action.ACTION_PQC_RESEND_EMAIL]);

/**
 * Maps action names to the API method key that requires a non-standard permission.
 * Actions listed here will be filtered from the UI if the user lacks the required permission.
 * Actions NOT listed here use the default case_record:write permission and are always shown.
 */
export const RESUBMISSION_RESTRICTED_ACTIONS: Record<string, string> = {
  pqc_resend_email: 'ManualActionService.ExecuteResendEmailAction',
};

/** Actions that belong to the attach-to-case endpoint */
const ATTACH_TO_CASE_ACTIONS = new Set<Action>([Action.ACTION_PQC_ATTACH_TO_CASE]);

/** Actions that belong to the regenerate-case-summary endpoint */
const REGENERATE_CASE_SUMMARY_ACTIONS = new Set<Action>([Action.ACTION_REGENERATE_CASE_SUMMARY]);

/** Actions that belong to the portal-session endpoint */
const PORTAL_SESSION_ACTIONS = new Set<Action>([
  Action.ACTION_PQC_START_PORTAL_SESSION,
  Action.ACTION_PQC_CLOSE_PORTAL_SESSION,
]);

/**
 * Get the correct service method for an action based on its task-type endpoint.
 * Cross-cutting actions (REASSIGN_TASK, CANCEL_CALL, CANCEL_WORKFLOW) use the legacy endpoint.
 */
export const getServiceMethodForAction = (action: Action): ExecuteActionFn => {
  if (AUDIT_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeAuditAction(req, opts);
  }
  if (CALL_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeCallAction(req, opts);
  }
  if (ELIGIBILITY_CHECK_AUDIT_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeEligibilityCheckAuditAction(req, opts);
  }
  if (INTAKE_DRAFT_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeIntakeDraftAction(req, opts);
  }
  if (REVIEW_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeReviewAction(req, opts);
  }
  if (RESEND_EMAIL_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeResendEmailAction(req, opts);
  }
  if (ATTACH_TO_CASE_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeAttachToCaseAction(req, opts);
  }
  if (REGENERATE_CASE_SUMMARY_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executeRegenerateCaseSummaryAction(req, opts);
  }
  if (PORTAL_SESSION_ACTIONS.has(action)) {
    return (req, opts) => manualActionService.executePortalSessionAction(req, opts);
  }
  // Workflow actions (REASSIGN_TASK, CANCEL_CALL, CANCEL_WORKFLOW) and any unknown actions
  // fall back to the legacy endpoint
  return (req, opts) => caseRecordService.executeAction(req, opts);
};
